import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import { supabase } from './supabase.js';
import { cloudSetupMessage } from './cloudMessage.js';
import { Photo, photos } from './Editorial.jsx';

const audience = [
  ['resident', 'Resident or homeowner'],
  ['supplier', 'Supplier'],
  ['buyer', 'Commercial buyer'],
];

function Benefits() {
  return <div className="rs-benefits">
    <div><h3>Your records are shared safely</h3><p>Repairs and proposals are visible only to the accounts allowed to manage them.</p></div>
    <div><h3>Pick up where you left off</h3><p>Return to the repair or opportunity you came to manage.</p></div>
  </div>;
}

/** Sign in or create an account. `next` overrides the ?next= return path, for the inline sign-in gate. */
export function AccountAccess({ mode = 'sign-in', next = '', photo }) {
  const { user, configured } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isSignup = mode === 'sign-up';
  const nextPath = next || params.get('next') || '';
  const [name, setName] = useState('');
  const [kind, setKind] = useState(params.get('workspace') === 'supplier' ? 'supplier' : 'resident');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  if (user && !next) return <Navigate to={params.get('next') || '/account'} replace/>;
  const submit = async (event) => {
    event.preventDefault(); setError(''); setNotice(''); setBusy(true);
    try {
      if (!supabase) throw new Error(cloudSetupMessage());
      const response = isSignup
        ? await supabase.auth.signUp({ email: email.trim(), password, options: { data: { full_name: name.trim(), preferred_workspace: kind, ...(/^[0-9a-f]{48}$/.test(params.get('invite') || '') ? { outreach_invite: params.get('invite') } : {}) } } })
        : await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (response.error) throw response.error;
      if (isSignup && !response.data.session) {
        setNotice('Check your email to confirm your account. Your workspace will be available after confirmation.');
        return;
      }
      const target = nextPath || (kind === 'supplier' ? '/supplier/opportunities' : kind === 'buyer' ? '/commercial/manage' : '/repairs');
      navigate(target.startsWith('/') ? target : '/account', { replace: true });
    } catch (cause) { setError(cause?.message || cloudSetupMessage()); }
    finally { setBusy(false); }
  };
  const switchSearch = next ? `?next=${encodeURIComponent(next)}` : location.search;
  const messages = <>
    {error && <p className="account-form-error" role="alert">{error}</p>}
    {notice && <p className="account-save-status" role="status">{notice}</p>}
    {!configured && <p className="account-form-error" role="status">{cloudSetupMessage()}</p>}
  </>;
  const emailAndPassword = <>
    <label className="rs-field"><span>Email address</span><input id="auth-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required/></label>
    <label className="rs-field"><span>Password</span><input id="auth-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={isSignup ? 'new-password' : 'current-password'} minLength={8} required/></label>
  </>;

  if (isSignup) {
    return <div className="rs-container rs-auth is-signup">
      <section className="rs-auth-top">
        <div className="rs-auth-intro">
          <p className="rs-kicker is-plain">RESIDENT SERVICE 360 ACCOUNT</p>
          <h1 className="rs-title">Create your Resident Service 360 account.</h1>
          <p className="rs-auth-lede">Use one secure account to report repairs, find jobs or publish work.</p>
        </div>
        <aside className="rs-auth-aside">
          <Photo photo={photo || photos.signUp} className="rs-auth-aside-photo" eager/>
          <Benefits/>
        </aside>
      </section>
      <section className="rs-auth-card is-wide" aria-labelledby="signup-heading">
        <p className="rs-kicker is-plain">CREATE ACCOUNT</p>
        <p className="rs-auth-secure">SECURE SIGN IN</p>
        <h2 className="rs-auth-heading" id="signup-heading">Your details</h2>
        <p className="rs-auth-text">Choose how you plan to use Resident Service 360.</p>
        <form onSubmit={submit} className="rs-signup-form">
          <div className="rs-role-box">
            <label className="rs-field"><span>I am joining as</span><select id="auth-workspace" value={kind} onChange={(event) => setKind(event.target.value)}>{audience.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
          </div>
          <div className="rs-form-stack">
            <label className="rs-field"><span>Your name</span><input id="auth-name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required/></label>
            {emailAndPassword}
            {messages}
            <div><button className="rs-button is-large" type="submit" disabled={busy || !configured}>{busy ? 'Please wait…' : 'Create account'}</button></div>
            <p className="rs-auth-switch">Already have an account? <Link to={`/sign-in${switchSearch}`}>Sign in</Link></p>
          </div>
        </form>
      </section>
    </div>;
  }

  return <div className="rs-container rs-auth is-signin">
    <section className="rs-auth-context">
      <Photo photo={photo || photos.signIn} className="rs-auth-context-photo"/>
      <div>
        <p className="rs-kicker is-plain">RESIDENT SERVICE 360 ACCOUNT</p>
        <h1 className="rs-title">Sign in to continue.</h1>
        <p className="rs-auth-lede">Open the right workspace for your repairs, company or buyer organisation.</p>
        <Benefits/>
      </div>
    </section>
    <section className="rs-auth-card is-narrow" aria-labelledby="signin-heading">
      <p className="rs-kicker is-plain">WELCOME BACK</p>
      <p className="rs-auth-secure">SECURE SIGN IN</p>
      <h2 className="rs-auth-heading" id="signin-heading">Your account</h2>
      <p className="rs-auth-text">Sign in to see your saved work.</p>
      <form onSubmit={submit} className="rs-form-stack">
        {emailAndPassword}
        <Link className="rs-text-link" to="/reset-password">Forgot your password?</Link>
        {messages}
        <div><button className="rs-button is-large" type="submit" disabled={busy || !configured}>{busy ? 'Please wait…' : 'Sign in'}</button></div>
      </form>
      <p className="rs-auth-switch">New to Resident Service 360? <Link to={`/sign-up${switchSearch}`}>Create an account</Link></p>
    </section>
  </div>;
}

/** Shared signed-out screen for pages that need an account. Signing in returns to the same page. */
export function SignInGate({ next }) {
  const location = useLocation();
  return <AccountAccess mode="sign-in" next={next || `${location.pathname}${location.search}`} photo={photos.accountGate}/>;
}
